import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { hasFrontendPermission } from '../permissions';

const STATUS_OPTIONS = ['new', 'assigned', 'in_progress', 'on_hold', 'completed'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'critical'];
const CATEGORY_OPTIONS = ['general', 'service', 'maintenance', 'inspection', 'install'];

const STATUS_LABELS = {
  new: 'New',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  completed: 'Completed',
};

const PRIORITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const priorityRank = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const fmtDate = (value) => {
  if (!value) return 'Unscheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const isDueToday = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const getCustomerName = (customers, customerId) => {
  if (!customerId) return 'No customer linked';
  const customer = customers.find((item) => item.id === customerId);
  return customer?.name || customerId;
};

const sortJobs = (jobs) => [...jobs].sort((a, b) => {
  const aHasDate = Boolean(a?.scheduledDate);
  const bHasDate = Boolean(b?.scheduledDate);
  if (aHasDate && bHasDate) {
    const dateDiff = String(a.scheduledDate).localeCompare(String(b.scheduledDate));
    if (dateDiff !== 0) return dateDiff;
  } else if (aHasDate !== bHasDate) {
    return aHasDate ? -1 : 1;
  }

  const priorityDiff = (priorityRank[a?.priority] ?? 99) - (priorityRank[b?.priority] ?? 99);
  if (priorityDiff !== 0) return priorityDiff;

  return String(a?.id || '').localeCompare(String(b?.id || ''));
});

const makeEmptyDraft = () => ({
  title: '',
  location: '',
  priority: 'medium',
  assignedTo: '',
  customerId: '',
  scheduledDate: '',
  notes: '',
  category: 'general',
});

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M3 6h6M6.5 3l2.5 3-2.5 3" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function JobsStat({ label, value, tone = 'default' }) {
  return (
    <article className={`jobs-stat jobs-stat--${tone}`}>
      <span className="jobs-stat-label">{label}</span>
      <strong className="jobs-stat-value">{value}</strong>
    </article>
  );
}

function JobListItem({ job, active, customerName, onSelect }) {
  return (
    <button
      type="button"
      className={`jobs-list-item${active ? ' is-active' : ''}`}
      onClick={() => onSelect(job.id)}
    >
      <div className="jobs-list-topline">
        <span className={`jobs-pill jobs-pill--priority-${job.priority || 'medium'}`}>
          {PRIORITY_LABELS[job.priority] || 'Medium'}
        </span>
        <span className="jobs-list-id">{job.id}</span>
      </div>
      <h3 className="jobs-list-title">{job.title || 'Untitled job'}</h3>
      <div className="jobs-list-meta">
        <span>{customerName}</span>
        <span>{fmtDate(job.scheduledDate)}</span>
      </div>
      <div className="jobs-list-meta">
        <span>{job.location || 'No location'}</span>
        <span className={`jobs-pill jobs-pill--status-${job.status || 'new'}`}>
          {STATUS_LABELS[job.status] || job.status || 'New'}
        </span>
      </div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="jobs-field">
      <span className="jobs-field-label">{label}</span>
      {children}
    </label>
  );
}

export function JobsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageJobs = hasFrontendPermission(user, 'jobs.manage');
  const canExecuteOwnJobs = hasFrontendPermission(user, 'jobs.execute.own');

  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [draft, setDraft] = useState(makeEmptyDraft);
  const createCardRef = useRef(null);
  const createTitleInputRef = useRef(null);

  const closeCreateForm = useCallback(() => {
    setShowCreateForm(false);
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('compose') === '1') {
      navigate('/jobs', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [jobsData, customersData] = await Promise.all([
        apiFetch('/api/jobs', { token }),
        apiFetch('/api/customers', { token }).catch(() => []),
      ]);
      setJobs(Array.isArray(jobsData) ? sortJobs(jobsData) : []);
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (e) {
      setError(e.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  useEffect(() => {
    if (!canManageJobs) return;
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('compose') !== '1') return;
    setShowCreateForm(true);
    window.requestAnimationFrame(() => {
      createCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      createTitleInputRef.current?.focus();
    });
  }, [canManageJobs, location.search]);

  const filteredJobs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return jobs.filter((job) => {
      if (statusFilter && job.status !== statusFilter) return false;
      if (priorityFilter && job.priority !== priorityFilter) return false;
      if (!term) return true;
      return [
        job.id,
        job.title,
        job.location,
        job.customerId,
        job.assignedTo,
        getCustomerName(customers, job.customerId),
      ].some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [jobs, customers, searchTerm, statusFilter, priorityFilter]);

  useEffect(() => {
    if (!filteredJobs.length) {
      setSelectedJobId('');
      return;
    }
    if (!filteredJobs.some((job) => job.id === selectedJobId)) {
      setSelectedJobId(filteredJobs[0].id);
    }
  }, [filteredJobs, selectedJobId]);

  const selectedJob = useMemo(
    () => filteredJobs.find((job) => job.id === selectedJobId) || null,
    [filteredJobs, selectedJobId],
  );

  const stats = useMemo(() => ({
    total: jobs.length,
    open: jobs.filter((job) => job.status !== 'completed').length,
    inProgress: jobs.filter((job) => job.status === 'in_progress').length,
    dueToday: jobs.filter((job) => isDueToday(job.scheduledDate)).length,
    unassigned: jobs.filter((job) => job.status !== 'completed' && !job.assignedTo).length,
  }), [jobs]);
  const quickActions = useMemo(() => {
    const actions = [];
    if (canManageJobs) {
      actions.push({
        label: showCreateForm ? 'Hide Create Form' : 'Create Job',
        onClick: () => setShowCreateForm((value) => !value),
      });
    }
    if (stats.unassigned > 0) {
      actions.push({
        label: 'Focus Unassigned',
        onClick: () => {
          setStatusFilter('');
          setPriorityFilter('');
          setSearchTerm('');
        },
      });
    }
    if (stats.dueToday > 0) {
      actions.push({
        label: 'Review Today',
        onClick: () => {
          const todayJob = jobs.find((job) => isDueToday(job.scheduledDate));
          if (todayJob) setSelectedJobId(todayJob.id);
        },
      });
    }
    return actions.slice(0, 3);
  }, [canManageJobs, showCreateForm, stats.unassigned, stats.dueToday, jobs]);

  const updateSelectedJobField = (key, value) => {
    setJobs((prev) => prev.map((job) => (
      job.id === selectedJobId ? { ...job, [key]: value } : job
    )));
  };

  const handleRefresh = () => {
    setRefreshKey((value) => value + 1);
  };

  const handleSaveJob = async () => {
    if (!selectedJob || !token || !canManageJobs) return;
    setSaving(true);
    setError('');
    try {
      const updated = await apiFetch(`/api/jobs/${encodeURIComponent(selectedJob.id)}`, {
        token,
        method: 'PUT',
        body: {
          title: selectedJob.title,
          priority: selectedJob.priority,
          status: selectedJob.status,
          assignedTo: selectedJob.assignedTo || '',
          location: selectedJob.location || '',
          customerId: selectedJob.customerId || '',
          scheduledDate: selectedJob.scheduledDate || '',
          category: selectedJob.category || 'general',
          notes: selectedJob.notes || '',
        },
      });
      setJobs((prev) => sortJobs(prev.map((job) => (job.id === updated.id ? updated : job))));
      pushToast(`Saved ${selectedJob.id}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to save job');
      pushToast(e.message || 'Failed to save job', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTechnicianStatus = async (status) => {
    if (!selectedJob || !token || !canExecuteOwnJobs) return;
    setSaving(true);
    setError('');
    try {
      const updated = await apiFetch(`/api/jobs/${encodeURIComponent(selectedJob.id)}/status`, {
        token,
        method: 'PATCH',
        body: { status },
      });
      setJobs((prev) => sortJobs(prev.map((job) => (job.id === updated.id ? updated : job))));
      pushToast(`${selectedJob.id} moved to ${STATUS_LABELS[status].toLowerCase()}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to update job status');
      pushToast(e.message || 'Failed to update job status', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateJob = async (event) => {
    event.preventDefault();
    if (!token || !canManageJobs) return;
    setSaving(true);
    setError('');
    try {
      const created = await apiFetch('/api/jobs', {
        token,
        method: 'POST',
        body: draft,
      });
      setJobs((prev) => sortJobs([created, ...prev]));
      setSelectedJobId(created.id);
      setDraft(makeEmptyDraft());
      closeCreateForm();
      pushToast(`Created ${created.id}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to create job');
      pushToast(e.message || 'Failed to create job', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="jobs-page">
        <Skeleton lines={12} />
      </section>
    );
  }

  return (
    <section className="jobs-page">
      <div className="jobs-header">
        <div>
          <span className="jobs-eyebrow">Operations</span>
          <h1 className="jobs-heading">Jobs</h1>
          <p className="jobs-subheading">
            Active field work, scheduling pressure, and technician execution in one queue.
          </p>
        </div>
        <div className="jobs-header-actions">
          <Button variant="secondary" onClick={handleRefresh} disabled={loading || saving}>
            <RefreshIcon />
            Refresh
          </Button>
          {canManageJobs ? (
            <Button onClick={() => setShowCreateForm((value) => !value)}>
              <PlusIcon />
              New Job
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <ErrorState message={error} onRetry={loadData} /> : null}

      <Card className="workflow-guide-card" title="What To Do Here">
        <div className="workflow-guide-head">
          <p className="workflow-guide-copy">
            Use Jobs to triage field work, confirm ownership, and keep every active visit moving toward completion.
          </p>
          <div className="workflow-guide-actions">
            {quickActions.map((action) => (
              <Button key={action.label} variant="secondary" size="sm" onClick={action.onClick}>
                {action.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="workflow-guide-list">
          <div className="workflow-guide-item">Start with unassigned or due-today work before editing lower-priority jobs.</div>
          <div className="workflow-guide-item">Use the queue to select one job, then confirm assignment, status, and customer context.</div>
          <div className="workflow-guide-item">Save manager changes here, or use the quick status buttons if you are the assigned technician.</div>
        </div>
      </Card>

      <div className="jobs-stats-grid">
        <JobsStat label="Total" value={stats.total} />
        <JobsStat label="Open" value={stats.open} tone="accent" />
        <JobsStat label="In Progress" value={stats.inProgress} tone="info" />
        <JobsStat label="Due Today" value={stats.dueToday} tone="warning" />
        <JobsStat label="Unassigned" value={stats.unassigned} tone="danger" />
      </div>

      {showCreateForm ? (
        <div ref={createCardRef}>
        <Card className="jobs-create-card" title="Create Job">
          <form className="jobs-form-grid" onSubmit={handleCreateJob}>
            <Field label="Title">
              <input ref={createTitleInputRef} className="jobs-input" value={draft.title} onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))} placeholder="Emergency rooftop AC repair" required />
            </Field>
            <Field label="Location">
              <input className="jobs-input" value={draft.location} onChange={(event) => setDraft((prev) => ({ ...prev, location: event.target.value }))} placeholder="1420 Westlake Ave" />
            </Field>
            <Field label="Priority">
              <select className="jobs-input" value={draft.priority} onChange={(event) => setDraft((prev) => ({ ...prev, priority: event.target.value }))}>
                {PRIORITY_OPTIONS.map((option) => <option key={option} value={option}>{PRIORITY_LABELS[option]}</option>)}
              </select>
            </Field>
            <Field label="Scheduled Date">
              <input className="jobs-input" type="date" value={draft.scheduledDate} onChange={(event) => setDraft((prev) => ({ ...prev, scheduledDate: event.target.value }))} />
            </Field>
            <Field label="Assigned To">
              <input className="jobs-input" value={draft.assignedTo} onChange={(event) => setDraft((prev) => ({ ...prev, assignedTo: event.target.value }))} placeholder="technician" />
            </Field>
            <Field label="Customer">
              <select className="jobs-input" value={draft.customerId} onChange={(event) => setDraft((prev) => ({ ...prev, customerId: event.target.value }))}>
                <option value="">Unlinked customer</option>
                {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <select className="jobs-input" value={draft.category} onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))}>
                {CATEGORY_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </Field>
            <Field label="Notes">
              <textarea className="jobs-input jobs-textarea" value={draft.notes} onChange={(event) => setDraft((prev) => ({ ...prev, notes: event.target.value }))} placeholder="Scope, access details, customer expectations" />
            </Field>
            <div className="jobs-form-actions">
              <Button variant="ghost" onClick={closeCreateForm}>Cancel</Button>
              <Button type="submit" disabled={saving}>Create Job</Button>
            </div>
          </form>
        </Card>
        </div>
      ) : null}

      <div className="jobs-layout">
        <Card className="jobs-list-card" title="Queue">
          <div className="jobs-toolbar">
            <input className="jobs-input jobs-search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search by job, customer, technician, location" />
            <div className="jobs-filter-row">
              <select className="jobs-input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{STATUS_LABELS[option]}</option>)}
              </select>
              <select className="jobs-input" value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value)}>
                <option value="">All priorities</option>
                {PRIORITY_OPTIONS.map((option) => <option key={option} value={option}>{PRIORITY_LABELS[option]}</option>)}
              </select>
            </div>
          </div>

          {filteredJobs.length ? (
            <div className="jobs-list">
              {filteredJobs.map((job) => (
                <JobListItem key={job.id} job={job} active={job.id === selectedJobId} customerName={getCustomerName(customers, job.customerId)} onSelect={setSelectedJobId} />
              ))}
            </div>
          ) : (
            <EmptyState title="No jobs match the current view" message="Try widening the filters or create a new job to seed the board." />
          )}
        </Card>

        <Card className="jobs-detail-card" title={selectedJob ? selectedJob.title : 'Details'}>
          {!selectedJob ? (
            <EmptyState title="No job selected" message="Select a job from the queue to inspect status, assignment, and notes." />
          ) : (
            <div className="jobs-detail">
              <div className="jobs-detail-head">
                <div className="jobs-detail-meta">
                  <span className="jobs-list-id">{selectedJob.id}</span>
                  <div className="jobs-detail-pills">
                    <span className={`jobs-pill jobs-pill--priority-${selectedJob.priority || 'medium'}`}>{PRIORITY_LABELS[selectedJob.priority] || 'Medium'}</span>
                    <span className={`jobs-pill jobs-pill--status-${selectedJob.status || 'new'}`}>{STATUS_LABELS[selectedJob.status] || selectedJob.status}</span>
                  </div>
                </div>
                <p className="jobs-detail-copy">{selectedJob.notes || 'No notes captured yet.'}</p>
              </div>

              <div className="jobs-detail-grid">
                <Field label="Customer">
                  {canManageJobs ? (
                    <select className="jobs-input" value={selectedJob.customerId || ''} onChange={(event) => updateSelectedJobField('customerId', event.target.value)}>
                      <option value="">Unlinked customer</option>
                      {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                    </select>
                  ) : <div className="jobs-readout">{getCustomerName(customers, selectedJob.customerId)}</div>}
                </Field>
                <Field label="Scheduled">
                  {canManageJobs ? (
                    <input className="jobs-input" type="date" value={selectedJob.scheduledDate || ''} onChange={(event) => updateSelectedJobField('scheduledDate', event.target.value)} />
                  ) : <div className="jobs-readout">{fmtDate(selectedJob.scheduledDate)}</div>}
                </Field>
                <Field label="Assigned To">
                  {canManageJobs ? (
                    <input className="jobs-input" value={selectedJob.assignedTo || ''} onChange={(event) => updateSelectedJobField('assignedTo', event.target.value)} />
                  ) : <div className="jobs-readout">{selectedJob.assignedTo || 'Unassigned'}</div>}
                </Field>
                <Field label="Location">
                  {canManageJobs ? (
                    <input className="jobs-input" value={selectedJob.location || ''} onChange={(event) => updateSelectedJobField('location', event.target.value)} />
                  ) : <div className="jobs-readout">{selectedJob.location || 'No location'}</div>}
                </Field>
                <Field label="Priority">
                  {canManageJobs ? (
                    <select className="jobs-input" value={selectedJob.priority || 'medium'} onChange={(event) => updateSelectedJobField('priority', event.target.value)}>
                      {PRIORITY_OPTIONS.map((option) => <option key={option} value={option}>{PRIORITY_LABELS[option]}</option>)}
                    </select>
                  ) : <div className="jobs-readout">{PRIORITY_LABELS[selectedJob.priority] || 'Medium'}</div>}
                </Field>
                <Field label="Status">
                  {canManageJobs ? (
                    <select className="jobs-input" value={selectedJob.status || 'new'} onChange={(event) => updateSelectedJobField('status', event.target.value)}>
                      {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{STATUS_LABELS[option]}</option>)}
                    </select>
                  ) : <div className="jobs-readout">{STATUS_LABELS[selectedJob.status] || selectedJob.status}</div>}
                </Field>
              </div>

              <Field label="Notes">
                {canManageJobs ? (
                  <textarea className="jobs-input jobs-textarea" value={selectedJob.notes || ''} onChange={(event) => updateSelectedJobField('notes', event.target.value)} />
                ) : <div className="jobs-readout jobs-readout--notes">{selectedJob.notes || 'No notes'}</div>}
              </Field>

              <div className="jobs-detail-actions">
                {canManageJobs ? (
                  <Button onClick={handleSaveJob} disabled={saving}>
                    <ArrowIcon />
                    Save Changes
                  </Button>
                ) : null}
                {canExecuteOwnJobs && selectedJob.assignedTo === user?.username ? (
                  <div className="jobs-quick-actions">
                    {['assigned', 'in_progress', 'completed'].map((status) => (
                      <Button key={status} variant={selectedJob.status === status ? 'primary' : 'secondary'} size="sm" onClick={() => handleTechnicianStatus(status)} disabled={saving}>
                        {STATUS_LABELS[status]}
                      </Button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
